import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Play,
  UserCircle,
  FileText,
  Calendar,
  Stethoscope,
  Users,
  ClipboardList,
  Video,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import VideoPlayer from '../components/VideoPlayer';
import {
  fadeInUp,
  staggerContainer,
  fastStaggerContainer,
  scaleIn,
} from '../utils/animations';

export default function Training() {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        setUserRole(profile?.role || null);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error checking user role:', error);
      setLoading(false);
    }
  };

  const patientGuides = [
    {
      title: 'Înregistrare și Autentificare',
      description:
        'Cum să vă creați un cont și să vă autentificați în platformă',
      icon: UserCircle,
      videoUrl:
        'https://files.decizieoncologica.ro/video/inregistrare-pacient.mp4',
    },
    {
      title: 'Încărcarea Documentelor',
      description:
        'Cum să încărcați documentele medicale necesare pentru consultație',
      icon: FileText,
      videoUrl:
        'https://files.decizieoncologica.ro/video/incarcare-documente-pacient.mp4',
    },
    {
      title: 'Programarea Consultației',
      description: 'Cum să programați o consultație cu echipa medicală',
      icon: Calendar,
      videoUrl:
        'https://files.decizieoncologica.ro/video/programare-consultatie-pacient.mp4',
    },
    {
      title: 'Participarea la Consultație',
      description: 'Cum să participați la consultația online',
      icon: Play,
      videoUrl:
        'https://files.decizieoncologica.ro/video/participare-pacient.mp4',
    },
  ];

  const doctorGuides = [
    {
      title: 'Gestionarea Pacienților',
      description:
        'Cum să gestionați lista de pacienți și să accesați dosarele medicale',
      icon: Users,
      videoUrl:
        'https://files.decizieoncologica.ro/video/gestionare-pacienti-medici.mp4',
    },
    {
      title: 'Programarea Consultațiilor',
      description: 'Cum să gestionați calendarul și programările pacienților',
      icon: Calendar,
      videoUrl:
        'https://files.decizieoncologica.ro/video/gestionare pacienti-medici.mp4',
    },
    {
      title: 'Scrisorile Medicale',
      description:
        'Cum să creați și să gestionați scrisorile medicale pentru pacienți',
      icon: ClipboardList,
      videoUrl:
        'https://files.decizieoncologica.ro/video/scrisoare-medicala-medic.mp4',
    },
    {
      title: 'Link-ul către Consultația Online',
      description:
        'Cum să participați și să gestionați consultațiile online cu pacienții',
      icon: Video,
      videoUrl: 'https://files.decizieoncologica.ro/video/calendar-medic.mp4',
    },
  ];

  const isDirectVideo = (url: string) => {
    return url.endsWith('.mp4');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007FFF]"></div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-50 via-blue-50/30 to-gray-50 py-16 overflow-hidden">
      {/* Subtle Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl"
          animate={{
            y: [-10, 10, -10],
            transition: {
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute bottom-40 right-20 w-80 h-80 bg-cyan-400/8 rounded-full blur-3xl"
          animate={{
            y: [10, -10, 10],
            transition: {
              duration: 10,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 w-72 h-72 bg-purple-400/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
            transition: {
              duration: 12,
              repeat: Infinity,
              ease: 'easeInOut',
            },
          }}
        />

        <div className="absolute inset-0 bg-[linear-gradient(rgba(2,73,148,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(2,73,148,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />

        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -80, 0],
              opacity: [0, 0.6, 0],
              transition: {
                duration: 5 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
                ease: 'easeInOut',
              },
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Patient Section */}
        <motion.div
          className="mb-20"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div className="text-center mb-16" variants={staggerContainer}>
            <motion.h1
              className="text-4xl font-bold text-gray-900 mb-4"
              variants={fadeInUp}
            >
              Ghid de Utilizare pentru Pacienți
            </motion.h1>
            <motion.p
              className="text-xl text-gray-600 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              Învățați cum să utilizați platforma noastră pentru a beneficia de
              cele mai bune servicii medicale
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-50px' }}
            variants={fastStaggerContainer}
          >
            {patientGuides.map((guide, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
                variants={scaleIn}
                whileHover={{ y: -5, transition: { duration: 0.3 } }}
              >
                <div className="aspect-video w-full">
                  {isDirectVideo(guide.videoUrl) ? (
                    <VideoPlayer url={guide.videoUrl} title={guide.title} />
                  ) : (
                    <iframe
                      src={guide.videoUrl}
                      title={guide.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <guide.icon className="w-6 h-6 text-[#007FFF]" />
                    <h3 className="text-xl font-semibold text-gray-900">
                      {guide.title}
                    </h3>
                  </div>
                  <p className="text-gray-600">{guide.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Doctor Section - Only visible to doctors */}
        {userRole === 'doctor' && (
          <motion.div
            className="mt-20 pt-20 border-t border-gray-200"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div
              className="text-center mb-16"
              variants={staggerContainer}
            >
              <motion.h2
                className="text-4xl font-bold text-gray-900 mb-4"
                variants={fadeInUp}
              >
                Ghid pentru Medici
              </motion.h2>
              <motion.p
                className="text-xl text-gray-600 max-w-3xl mx-auto"
                variants={fadeInUp}
              >
                Instrucțiuni detaliate pentru utilizarea funcționalităților
                dedicate medicilor
              </motion.p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={fastStaggerContainer}
            >
              {doctorGuides.map((guide, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-[#007FFF]/10"
                  variants={scaleIn}
                  whileHover={{ y: -5, transition: { duration: 0.3 } }}
                >
                  <div className="aspect-video w-full">
                    {isDirectVideo(guide.videoUrl) ? (
                      <VideoPlayer url={guide.videoUrl} title={guide.title} />
                    ) : (
                      <iframe
                        src={guide.videoUrl}
                        title={guide.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <guide.icon className="w-6 h-6 text-[#007FFF]" />
                      <h3 className="text-xl font-semibold text-gray-900">
                        {guide.title}
                      </h3>
                    </div>
                    <p className="text-gray-600">{guide.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
